/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import {
  AlertComponent,
  BreadcrumbComponent,
  PaginationComponent,
} from "../../common/components";
import { breadcrumbLink } from "./lead-capture-list.constant";
import LeadCaptureFilter from "./lead-capture-filter/lead-capture-filter";
import MUIBoxTable from "../../common/components/mui-table/mui-box-table";
import CustomActionsDropdown from "./customs-action-dropdown";
import "./lead-capture-form-list.scss";
import LeadCaptureFilterOptions from "./lead-capture-filter/lead-capture-filter-options";
import { leadCaptureFilterOptions } from "./lead-capture-filter/lead-capture-filter.constant";
import CreateLeadCapturePopupConfirmation from "./create-lead-capture-popups/create-lead-capture-popup-confirmation/create-lead-capture-popup-confirmation";
import { getLeadCaptureForms, deleteForm } from "../../api/lead-capture";
import { useSetLoading } from "../../common/hooks/appearance";
import LeadCapturedFormLeadList from "./lead-captured-form-lead-list/lead-captured-form-lead-list";
import CreateLeadCaptureForm from "./create-lead-capture-form/create-lead-capture-form";
import ConfirmationPopup from "../../common/components/confirmation-popup/confirmation-popup";
import { formatDate } from "../../utils/utils";
import { useLogin } from "../../common/hooks/useUser";
import EmailSetUpBanner from "../../common/components/email-setup-required-banner/email-setup-banner";

const LeadCaptureFormList = () => {
  const [crumbMappingOption, setCrumbMappingOption] =
    useState<any>(breadcrumbLink);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [totalRecords, setTotalRecords] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLeadsFilter, setSelectedLeadsFilters] = useState<number[]>([]);
  const [selectedLeadsFilterOptions, setSelectedLeadsFilterOptions] = useState<
    any[]
  >(leadCaptureFilterOptions);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [data, setData] = useState<any[]>([]);
  const [isViewMode, setIsViewMode] = useState<any>(false);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [isShowConfirmationPopup, setIsShowConfirmationPopup] = useState(false);
  const [selectedLeadCaptureFormId, setSelectedLeadCaptureFormId] =
    useState<any>(null);
  const [selectedLeadCaptureFormName, setSelectedLeadCaptureFormName] = useState<string>("");
  const [isShowCreateLeadSection, setIsShowCreateLeadSection] =
    useState<boolean>(false);
  const [createLeadCaptureInitialValues, setCreateLeadCaptureInitialValues] =
    useState<any>(null);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [selectedArchiveForm, setSelectedArchiveForm] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const isSearchTriggered = useRef(false);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleArchive = (formId: number, formName: string) => {
    setSelectedArchiveForm({ id: formId, name: formName });
    setIsArchivePopupOpen(true);
  };

  const handleArchiveAPI = async (formId: number) => {
    try {
      await deleteForm(formId);
      setAlertMessage(
        `"${selectedArchiveForm?.name}" has been archived successfully.`
      );
      setAlertType("success");
      setAlertOpen(true);
      fetchLeadCaptureForms(currentPage, searchText, activeFilters);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to archive form."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsArchivePopupOpen(false);
    }
  };

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
      isSearchTriggered.current = true;
      setCurrentPage(1);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };
  const loginDetails = useLogin();
  const isEmailConfigured = loginDetails?.isEmailConfigured;

  const headers = [
    {
      key: "actions",
      label: "",
      render: (row: any) => (
        <CustomActionsDropdown
          row={row}
          setCreateLeadCaptureInitialValues={setCreateLeadCaptureInitialValues}
          setIsShowCreateLeadSection={setIsShowCreateLeadSection}
          setIsShowConfirmationPopup={setIsShowConfirmationPopup}
          setArchive={() => handleArchive(row.id, row.data.form_title)}
          isEmailConfigured={isEmailConfigured}
        />
      ),
    },
    { key: "formName", label: "Form Name" },
    { key: "createdBy", label: "Created By" },
    { key: "createdOn", label: "Created On" },
    { key: "leadCaptured", label: "Leads Captured" },
    { key: "lastSubmission", label: "Last Submission" },
  ];

  const fetchLeadCaptureForms = async (
    page = 1,
    search: string = "",
    filters: any = {}
  ) => {
    setLoading(true);
    setCrumbMappingOption(breadcrumbLink);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (search) {
        params.searchText = search;
      }
      if (filters.formName) {
        params.formName = filters.formName;
      }
      if (filters?.createdByUserIds?.length > 0) {
        params.createdBy = filters.createdByUserIds.map(Number);
      }
      if (filters?.createdOnStart && filters.createdOnEnd) {
        // if (filters.createdOnStart.isSame(filters.createdOnEnd)) {
        params.createdTimeStart = filters.createdOnStart.startOf("day");
        params.createdTimeEnd = filters.createdOnEnd.endOf("day");
        // } else {
        //   params.createdTimeStart = filters.createdOnStart;
        //   params.createdTimeEnd = filters.createdOnEnd
        // }
      }
      if (filters?.lastSubmissionStart && filters.lastSubmissionEnd) {
        // if (filters.lastSubmissionStart.isSame(filters.lastSubmissionEnd)) {
        params.lastSubmissionStart = filters.lastSubmissionStart.startOf("day");
        params.lastSubmissionEnd = filters.lastSubmissionEnd.endOf("day");
        // } else {
        //   params.lastSubmissionTimeStart = filters.lastSubmissionStart;
        //   params.lastSubmissionTimeEnd = filters.lastSubmissionEnd
        // }
      }
      const response = await getLeadCaptureForms(params);
      setData(response.data.data.data);
      setTotalRecords(response.data.data.total);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns"
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!viewOpen) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchLeadCaptureForms(pageToUse, searchText, activeFilters);
      isSearchTriggered.current = false;
    }
  }, [currentPage, searchText, activeFilters, viewOpen]);
  const fetchFullName = (item: any) => {
    const firstName = item?.first_name?.trim();
    const lastName = item?.last_name?.trim();
    const fullName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName
          ? firstName
          : lastName
            ? lastName
            : "-";
    return fullName;
  };

  const handleViewClick = async (formId: number, formTitle: string) => {
    setSelectedLeadCaptureFormId(formId);
    setSelectedLeadCaptureFormName(formTitle);
    // setCrumbMappingOption([
    //   ...crumbMappingOption,
    //   { label: "Leads", link: "" },
    // ]);
    setViewOpen(!viewOpen);
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        const leadCount = item.leadCount ?? "-";
        const leadCountForForm = (
          <span
            style={{
              cursor: leadCount > 0 ? "pointer" : "default",
              color: "#1194DA",
              textDecoration: leadCount === 0 ? "none" : "underline",
            }}
            onClick={() => {
              if (leadCount > 0) {
                handleViewClick(item.id, item.form_title);
              }
            }}
          >
            {leadCount}
          </span>
        );
        const formTitle = (
          <span
            style={{
              cursor: "pointer",
            }}
            onClick={() => {
              setIsViewMode(true);
              setIsShowCreateLeadSection(true);
              setCreateLeadCaptureInitialValues(item);
            }}
          >
            {item.form_title || "-"}
          </span>
        );
        return {
          id: item.id,
          formName: formTitle,
          createdOn: formatDate(item.created_at) || "-",
          leadCaptured: leadCountForForm,
          lastSubmission: formatDate(item.submission_date) || "-",
          createdBy: fetchFullName(item.createdBy),
          data: item,
        };
      });
    }
    return [];
  }, [data]);

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading LeadCapture Forms...";
    }
    if (searchText) {
      return "No lead forms found matching your search";
    }
    return "No lead capture forms available, Please create a form by clicking on Create New Form";
  }, [loading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.lastSubmissionStart || activeFilters.lastSubmissionEnd;
    const runFilterActive =
      activeFilters.createdOnStart || activeFilters.createdOnEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdOnStart;
    delete otherFilters.createdOnEnd;
    delete otherFilters.lastSubmissionStart;
    delete otherFilters.lastSubmissionEnd;

    const otherActiveFilters = Object.values(otherFilters).filter((val) => {
      if (val === undefined || val === "" || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") {
        return Object.values(val).some(
          (v) => v !== "" && v !== undefined && v !== null
        );
      }
      return true;
    }).length;
    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);
  
  const fetchFormsWithFiltersAndSearch = () => {
    if (currentPage === 1) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchLeadCaptureForms(pageToUse, searchText, activeFilters);
      isSearchTriggered.current = false;
    } else {
      setCurrentPage(1);
    }
  }
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      {isShowCreateLeadSection ? (
        <CreateLeadCaptureForm
          setCreateLeadCaptureInitialValues={setCreateLeadCaptureInitialValues}
          createLeadCaptureInitialValues={createLeadCaptureInitialValues}
          setIsShowCreateLeadSection={setIsShowCreateLeadSection}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setAlertOpen={setAlertOpen}
          setLoading={setLoading}
          fetchLeadCaptureForms={()=>{
            setCurrentPage(1);
            fetchLeadCaptureForms()
          }}
          isViewMode={isViewMode}
          setIsViewMode={setIsViewMode}
        />
      ) : (
        ""
      )}
      <div
        className="lead-capture-form-wrapper"
        style={{ display: isShowCreateLeadSection ? "none" : "block" }}
      >
        {!viewOpen && (
          <div className="breadcrumb-wrapper">
            <BreadcrumbComponent
              breadcrumbLink={crumbMappingOption}
              showBackArrow={false}
            />
          </div>
        )}

        <div style={{ display: !viewOpen ? "block" : "none" }}>
          <LeadCaptureFilter
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            selectedLeadsFilter={appliedFilterCount}
            onSearch={handleSearch}
            searchRef={searchRef}
            isResetSearch={isResetSearch}
            resetSearch={resetSearch}
            handleClickCreateNewForm={() => {
              setIsShowCreateLeadSection(true);
            }}
            fetchData={fetchFormsWithFiltersAndSearch}
          />

          <div className="lead-capture-table-filter">
            {!isEmailConfigured && !isViewMode && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
            <div
              className="lead-capture-table"
              style={isOpenFilter ? { width: "90%" } : {}}
            >
              <MUIBoxTable
                columns={headers}
                data={formattedData}
                noResultText={noResultText}
              />
            </div>
            {isShowConfirmationPopup ? (
              <div>
                <CreateLeadCapturePopupConfirmation
                  isShowConfirmationPopup={isShowConfirmationPopup}
                  setIsShowConfirmationPopup={setIsShowConfirmationPopup}
                  createLeadCaptureId={createLeadCaptureInitialValues?.id}
                  setCreateLeadCaptureInitialValues={
                    setCreateLeadCaptureInitialValues
                  }
                  formLink={createLeadCaptureInitialValues?.form_link}
                  setAlertMessage={setAlertMessage}
                  setAlertType={setAlertType}
                  setAlertOpen={setAlertOpen}
                  setLoading={setLoading}
                  qrCodeUrl={createLeadCaptureInitialValues?.qr_code_url }
                  formName={createLeadCaptureInitialValues?.form_title}
                />
              </div>
            ) : (
              ""
            )}
            <div
              style={{ display: isOpenFilter ? "block" : "none" }}
              className="lead-capture-filter"
            >
              <LeadCaptureFilterOptions
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
                selectedLeadsFilter={selectedLeadsFilter}
                setSelectedLeadsFilters={setSelectedLeadsFilters}
                selectedLeadsFilterOptions={selectedLeadsFilterOptions}
                setAlertMessage={setAlertMessage}
                setAlertType={setAlertType}
                setAlertOpen={setAlertOpen}
                setSelectedLeadsFilterOptions={setSelectedLeadsFilterOptions}
                onApplyFilters={(filters: any) => {
                  setCurrentPage(1);
                  setActiveFilters(filters);
                }}
                activeFilters={activeFilters}
              />
            </div>
            {totalRecords && totalRecords > 0 ? (
              <div className="lead-capture-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>

        {viewOpen && (
          <LeadCapturedFormLeadList
            loading={loading}
            setLoading={setLoading}
            selectedLeadCaptureFormId={selectedLeadCaptureFormId}
            selectedLeadCaptureFormName={selectedLeadCaptureFormName}
            viewOpenForm={viewOpen}
            setViewOpenForm={(val: boolean) => {
              setViewOpen(val);
              setIsShowCreateLeadSection(false);
            }}
          />
        )}
      </div>
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
      <ConfirmationPopup
        open={isArchivePopupOpen}
        onCancel={() => setIsArchivePopupOpen(false)}
        onOkay={() => {
          if (selectedArchiveForm) {
            handleArchiveAPI(selectedArchiveForm.id);
          }
        }}
        title="Archive Form"
        note={`Are you sure you want to archive the form "${selectedArchiveForm?.name}"`}
        buttonText="Archive"
      />
    </>
  );
};

export default LeadCaptureFormList;
